import { observable, runInAction } from "mobx"

import { CodingGuidelinesDialogModel } from "@/components/CodingGuidelinesDialog/CodingGuidelinesDialogModel.ts"
import { ProjectGeneralTabModel } from "@/components/ProjectSettingsDialog/ProjectGeneralTabModel.ts"
import { ProjectMirroringModel } from "@/components/ProjectSettingsDialog/ProjectMirroringModel.ts"
import { ProjectPooledPreviewViewModel } from "@/components/ProjectSettingsDialog/ProjectPooledPreviewViewModel.ts"
import { ProjectPreviewSetupViewModel } from "@/components/ProjectSettingsDialog/ProjectPreviewSetupViewModel.ts"
import { ProjectRootFolderViewModel } from "@/components/ProjectSettingsDialog/ProjectRootFolderViewModel.ts"
import { isTabId, TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectPermissionsProp } from "@/routes/projects/projectHooks.ts"
import { ProjectSecretsViewModel } from "@/routes/projects/secrets/ProjectSecretsViewModel.ts"
import { WarmupSnapshotsViewModel } from "@/routes/projects/warmups/WarmupSnapshotsViewModel.ts"

import type { ProjectSettingsTabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import type { PR_Project2, RepositoryFetchStatus } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { ProjectPermissionsProp } from "@/routes/projects/projectHooks.ts"
import type { IObservableValue } from "mobx"
import type { MouseEvent } from "react"


export class ProjectSettingsDialogModel extends Model {
  private readonly tabModels = new Map<ProjectSettingsTabId, Model>()
  private permissions: ProjectPermissionsProp | null

  readonly currentTab: IObservableValue<ProjectSettingsTabId>

  constructor(readonly project: PR_Project2, initialTab: string | undefined, private readonly deps: TabConstructorDeps) {
    super("ProjectSettingsDialogModel")

    let tab: ProjectSettingsTabId = TabId.General
    if (initialTab && isTabId(initialTab)) {
      tab = initialTab
    }

    this.currentTab = observable.box(tab)
    this.permissions = null

    void this.subscribeToRepoSyncStatus()
  }

  readonly repoSyncStatus = observable.box<RepositoryFetchStatus | null>(null)

  private async subscribeToRepoSyncStatus() {
    try {
      const subscription = await this.deps.context.queries.subscribeToRepositorySyncErrors(this.project.key, this.project.repository.name)
      runInAction(() => {
        this.repoSyncStatus.set(subscription.initial)
      })

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.repoSyncStatus.set(status))
      }), "ProjectSettingsDialogModel:subscribeToRepoSyncStatus")
    } catch (e) {
      log.error("Failed to subscribe to repo sync status:", e)
    }
  }

  private getPermissions(): ProjectPermissionsProp {
    if (this.permissions == null) {
      this.permissions = projectPermissionsProp(this.deps.context.queries, this.project.key)
    }
    return this.permissions
  }

  setTab = (tab: ProjectSettingsTabId) => {
    runInAction(() => this.currentTab.set(tab))
  }

  onTabClick = (tab: ProjectSettingsTabId) => {
    return (e: MouseEvent) => {
      e.preventDefault()
      this.setTab(tab)
    }
  }

  get canEditProject() {
    return this.getPermissions().canEditProject
  }

  get canViewProject() {
    return this.getPermissions().canViewProject
  }

  get generalTabModel() {
    return this.getOrCreateTabModel<ProjectGeneralTabModel>(TabId.General, () =>
      new ProjectGeneralTabModel(this.deps.context, this.project, this.getPermissions()))
  }

  get codingGuidelinesTabModel() {
    return this.getOrCreateTabModel(TabId.CodingGuidelines, () => {
      return new CodingGuidelinesDialogModel(this.project, this.deps.context.queries)
    })
  }

  get mirroringTabModel() {
    return this.getOrCreateTabModel(TabId.Git, () => {
      return new ProjectMirroringModel(this.deps.context, this.project, this.deps.context.queries, this.canEditProject)
    })
  }

  get rootFolderTabModel() {
    return this.getOrCreateTabModel(TabId.RootFolder, () => {
      return new ProjectRootFolderViewModel(this.deps.context, this.canEditProject, this.project.key, this.project.repository.name)
    })
  }

  get previewSettingsTabModel() {
    return this.getOrCreateTabModel(TabId.PreviewSetup, () => {
      return new ProjectPreviewSetupViewModel(this.deps.context, this.project, this.canEditProject)
    })
  }

  get secretsTabModel() {
    return this.getOrCreateTabModel(TabId.Secrets, () => {
      return new ProjectSecretsViewModel(this.deps.context, this.project.key)
    })
  }

  get warmupSnapshotsTabModel() {
    return this.getOrCreateTabModel(TabId.WarmupSnapshots, () => {
      return new WarmupSnapshotsViewModel(this.deps.context, this.canEditProject, this.project)
    })
  }

  get pooledPreviewTabModel() {
    return this.getOrCreateTabModel(TabId.PooledPreview, () => {
      return new ProjectPooledPreviewViewModel(this.deps.context, this.project, this.canEditProject)
    })
  }

  private getOrCreateTabModel = <T extends Model>(tabId: ProjectSettingsTabId, create: () => T): T => {
    if (!this.tabModels.has(tabId)) {
      this.tabModels.set(tabId, create())
    }

    return this.tabModels.get(tabId) as T
  }

  terminate() {
    super.terminate()

    for (const model of this.tabModels.values()) {
      model.terminate()
    }
  }
}

type TabConstructorDeps = {
  context: OrgContext
}

const log = getLogger("ProjectSettingsDialogModel")